/* eslint-disable react/jsx-boolean-value */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import DropDownActionButton from '@components/dropdown-action-button/DropDownActionButton';
import { FEATURE_FLAGS } from '@constants/feature';
import { LEAD_MANAGEMENT } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  sendMixpanelEvent,
  trackPageView
} from '@core/analytics/mixpanel';
import { useAppDispatch, useAppSelector } from '@core/store';
import DeleteConfirmationModal from '@features/lead-management/components/lead-activity-panel/DeleteConfirmationModal';
import LeadsAddTagModal from '@features/lead-management/components/LeadsAddTagModal';
import MultipleLeadSelectionBottomBar from '@features/lead-management/components/MultipleLeadSelectionBottomBar';
import { LeadSectionId } from '@features/lead-management/constants';
import useMetaProvider from '@features/lead-management/hooks/useMetaProvider';
import useMetaSyncPolling from '@features/lead-management/hooks/useMetaSyncPolling';
import { addMetaSync } from '@features/lead-management/metaSyncSlice';
import {
  useDeleteLeadMutation,
  useLazyGetAgentsQuery,
  useLazyGetLeadsQuery,
  useLazyGetTagsQuery,
  useLeadBulkUpdateMutation,
  useUpdateLeadAssigneeMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import { setLeadListPage } from '@features/lead-management/slices/leadSlice';
import { LeadType } from '@features/lead-management/types/common';
import { buildListFilteredMixpanelPayload } from '@features/lead-management/utils/leadAnalytics';
import getSaveLeadErrorMessage from '@features/lead-management/utils/leadApiErrorMessages';
import {
  getMetaProviderErrorMessage,
  META_SYNC_STATUS
} from '@features/lead-management/utils/metaIntegrationHelpers';
import useNotify from '@hooks/notify';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';
import { Tag } from 'types/lead-management';

import LeadBulkUploadModal from './LeadBulkUploadModal';
import LeadListEmptyView from './LeadListEmptyView';
import LeadTable from './LeadTable';

import SetupMetaIntegrationModal from '../../components/lead-settings/SetupMetaIntegrationModal';
import LeadFilters from '../../components/LeadFilter';
import { getAddLeadOptionsForOrg } from '../../constants/leadList';
import useLeadAgentRestrictions from '../../hooks/useLeadAgentRestrictions';
import useLeadListFilters from '../../hooks/useLeadListFilters';

const LeadList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const page = useAppSelector((state) => state.lead.currentPage);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );
  const isMetaLeadIngestionEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.META_LEAD_INGESTION
  );

  const {
    isAssigneeSelectionDisabled,
    isLeadDeletionDisabled,
    isLoading: isRestrictionConfigLoading
  } = useLeadAgentRestrictions();

  const setPage = (newPage: number) => {
    dispatch(setLeadListPage(newPage));
  };

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<LeadSectionId>(
    LeadSectionId.LEADS
  );
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingLeads, setIsFetchingLeads] = useState(true);
  const [didInitialCheck, setDidInitialCheck] = useState(false);
  const [showEmptyFromInitial, setShowEmptyFromInitial] = useState(false);
  const [isMetaSetupModalOpen, setIsMetaSetupModalOpen] = useState(false);

  const { createSuccessAlert, createErrorAlert } = useNotify();

  const [getLeads, { data: leadsList, isLoading: isLeadsLoading }] =
    useLazyGetLeadsQuery();
  const [getTags, { data: tagsList }] = useLazyGetTagsQuery();
  const [getAgents, { data: agentsList }] = useLazyGetAgentsQuery();
  const [leadBulkUpdate] = useLeadBulkUpdateMutation();
  const [updateLeadAssignee] = useUpdateLeadAssigneeMutation();
  const [deleteLead] = useDeleteLeadMutation();
  const isFirstFetch = useRef(true);

  const {
    providerData,
    createProvider: createMetaProvider,
    updateProvider: updateMetaProvider
  } = useMetaProvider(organizationId, { skip: !isMetaLeadIngestionEnabled });
  const { pollMetaSyncStatus } = useMetaSyncPolling();

  const {
    search,
    selectedSources,
    selectedStatusIds,
    selectedTagIds,
    selectedAdIds,
    toQueryString,
    setSearch,
    setSelectedSources,
    setSelectedStatusIds,
    setSelectedTagIds,
    setSelectedAdIds,
    adsSelectMode,
    setAdsSelectMode,
    excludedAdIds,
    setExcludedAdIds,
    sourceOptions,
    statusOptions,
    tagOptions,
    adOptions,
    selectedAssigneeIds,
    setSelectedAssigneeIds,
    assigneeOptions,
    setDateRange,
    selectedDateRange,
    setDateRangeType,
    dateRangeType,
    fetchAds,
    loadMoreAds,
    adsHasMore,
    adsIsLoading,
    adsTotalCount
  } = useLeadListFilters();

  const handleFetchingLeads = async (isInitialFetch: boolean = false) => {
    setIsFetchingLeads(true);
    try {
      const pageToFetch = page ?? 0;
      const queryString = toQueryString({ page: pageToFetch });
      const response = await getLeads({ organizationId, queryString });
      if (response.data) {
        setPage(response.data.pagination?.page);
        if (isInitialFetch) {
          setDidInitialCheck(true);
          setShowEmptyFromInitial(!(response.data.data?.length > 0));
        }
      } else {
        setPage(pageToFetch);
      }
    } catch (error) {
      createErrorAlert('Failed to fetch leads. Please try again.');
    }
    setIsFetchingLeads(false);
  };

  const getDeleteModalMessage = () => {
    if (!selectedLeadId) return '';
    const selectedLead = leadsList?.data?.find(
      (lead: LeadType) => lead.id === selectedLeadId
    );
    return `Are you sure you want to delete ${selectedLead?.name}?`;
  };

  const handleDeleteLead = async (leadId: string) => {
    setSelectedLeadId(leadId);
    setIsDeleteModalOpen(true);
  };

  const onConfirmDeleteLead = async () => {
    try {
      await deleteLead({ organizationId, leadId: selectedLeadId }).unwrap();
      createSuccessAlert('Lead deleted successfully.');
      setIsDeleteModalOpen(false);
      setSelectedLeadId('');

      const currentData = leadsList?.data ?? [];
      const isLastItemOnPage = currentData.length === 1;
      const newPage = isLastItemOnPage && page > 0 ? page - 1 : page;

      setPage(newPage);
      getLeads({
        organizationId,
        queryString: toQueryString({ page: newPage })
      });
    } catch (error) {
      createErrorAlert('Failed to delete lead.');
    }
  };

  const handleMetaSyncComplete = async (status: string) => {
    if (
      status === META_SYNC_STATUS.COMPLETED ||
      status === META_SYNC_STATUS.PARTIAL_SUCCESS
    ) {
      const pageToFetch = page || 0;
      const queryString = toQueryString({ page: pageToFetch });
      const leadsResponse = await getLeads({
        organizationId,
        queryString
      });

      if (leadsResponse.data?.data?.length > 0) {
        setShowEmptyFromInitial(false);
      }
    }
  };

  const handleSyncFromMeta = async () => {
    if (!providerData) {
      setIsMetaSetupModalOpen(true);
      return;
    }

    try {
      const response = await updateMetaProvider({
        organizationId,
        providerId: providerData?.id ?? '',
        body: { is_active: true }
      }).unwrap();

      if (response?.job_id) {
        const syncId = `meta-sync-${Date.now()}`;
        dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

        await pollMetaSyncStatus({
          syncId,
          jobId: response.job_id,
          onComplete: handleMetaSyncComplete
        });
      }
    } catch (error) {
      createErrorAlert(getMetaProviderErrorMessage(error), {
        classOverride: 'max-w-xl'
      });
    }
  };

  const handleAddOption = (option: string) => {
    if (option === 'add-manually') {
      navigate(LEAD_MANAGEMENT.LEAD_ADD);
    } else if (option === 'import-csv') {
      setIsCsvUploadModalOpen(true);
    } else if (option === 'sync-from-meta') {
      handleSyncFromMeta();
    }
  };

  const handleCsvUploadSuccess = (isInitialFetch: boolean = false) => {
    setIsCsvUploadModalOpen(false);
    handleFetchingLeads();
    if (isInitialFetch) {
      setShowEmptyFromInitial(false);
    }
  };

  const handleCsvModalClose = () => {
    setIsCsvUploadModalOpen(false);
  };

  const handleMetaSetupConfirm = async (accessToken: string, date: Date) => {
    try {
      const response = await createMetaProvider({
        organizationId,
        body: {
          access_token: accessToken,
          is_active: true,
          initial_sync_date: date.toISOString()
        }
      }).unwrap();

      setIsMetaSetupModalOpen(false);

      if (response?.job_id) {
        const syncId = `meta-sync-${Date.now()}`;
        dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

        await pollMetaSyncStatus({
          syncId,
          jobId: response.job_id,
          onComplete: handleMetaSyncComplete
        });
      } else {
        createErrorAlert('Meta integration failed. Please try again.');
      }
    } catch (error) {
      createErrorAlert(getMetaProviderErrorMessage(error), {
        classOverride: 'max-w-xl'
      });
    }
  };

  const handleAddTagButtonClick = (leadId: string) => {
    setSelectedLeadId(leadId);
    setIsTagModalOpen(true);
  };

  const onTagsAction = () => {
    setSelectedSection(LeadSectionId.TAGS);
    setIsTagModalOpen(true);
  };

  const onAssigneesAction = () => {
    setSelectedSection(LeadSectionId.ASSIGNEES);
  };

  const onStatusAction = () => {
    setSelectedSection(LeadSectionId.STATUS);
  };

  const onLeadsAction = () => {
    setSelectedSection(LeadSectionId.LEADS);
  };

  const handleUpdateAssignee = async (
    leadId: string,
    assignee: { id: string; isUnassigned: boolean }
  ) => {
    if (!leadId || !assignee?.id) {
      createErrorAlert('Invalid assignee selection.');
      return;
    }
    try {
      await updateLeadAssignee({
        organizationId,
        leadId,
        assignee: assignee.isUnassigned ? null : assignee.id
      }).unwrap();

      // Mixpanel event for lead assigned
      const previousAssignee = leadsList?.data?.find(
        (lead: LeadType) => lead.id === leadId
      )?.assignee?.id;

      sendMixpanelEvent(Event.LEAD_ASSIGNED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        assignee_id: assignee.isUnassigned ? null : assignee.id,
        assignment_method: 'manual',
        previous_assignee: previousAssignee || null,
        is_reassignment: !!previousAssignee
      });
      getLeads({ organizationId, queryString: toQueryString({ page }) });
      createSuccessAlert('Assignee updated successfully.');
    } catch (err: any) {
      createErrorAlert(
        getSaveLeadErrorMessage(err, false, { updateContext: 'assignee' })
      );
    }
  };

  const handlePagination = (pageNumber: number) => {
    setIsFetchingLeads(true);
    try {
      const queryString = toQueryString({ page: pageNumber });
      getLeads({ organizationId, queryString }).then((response) => {
        if (response.data) {
          setPage(response.data.pagination?.page);
        }
      });
    } catch (error: any) {
      createErrorAlert(error?.data?.message || 'Failed to fetch leads.');
    }
    setIsFetchingLeads(false);
  };

  const handleFetchingTags = async (searchInput: string) => {
    await getTags({ organizationId, searchTerm: searchInput });
  };

  const handleFetchingAgents = async () => {
    await getAgents({ organizationId });
  };

  const getExistingTagsOfSelectedLead = () => {
    const selectedLead = leadsList?.data?.find(
      (lead: LeadType) => lead.id === selectedLeadId
    );
    return selectedLead?.tags;
  };

  const handleAddTags = (tags: Tag[]) => {
    try {
      leadBulkUpdate({
        leadIds: selectedRows.length ? selectedRows : [selectedLeadId],
        tagIds: tags.map((tag) => tag.id)
      }).then(() => {
        setIsTagModalOpen(false);
        setSelectedSection(LeadSectionId.LEADS);
        createSuccessAlert('Tags added to lead successfully');
      });
    } catch (error) {
      createErrorAlert('Failed to add tags to lead. Please try again.');
    }
  };

  useEffect(() => {
    handleFetchingTags('');
    handleFetchingAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleFetchingLeads(isFirstFetch.current);
    isFirstFetch.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    selectedSources,
    selectedStatusIds,
    selectedTagIds,
    selectedAssigneeIds,
    selectedAdIds,
    adsSelectMode,
    excludedAdIds,
    selectedDateRange,
    dateRangeType
  ]);

  // Mixpanel event for leads list viewed
  const hasTrackedLeads = useRef(false);
  useEffect(() => {
    const hasData = leadsList?.data?.length;
    if (!hasData || hasTrackedLeads.current || location.state?.fromLeadCreation)
      return;
    hasTrackedLeads.current = true;

    trackPageView('Leads list page viewed');
    sendMixpanelEvent(Event.LEADS_LIST_VIEWED, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      total_leads_shown: leadsList.data.length,
      total_leads_count: leadsList.pagination?.total_rows ?? 0,
      page_number: (leadsList.pagination?.page ?? 0) + 1,
      filters_active: buildListFilteredMixpanelPayload({
        status: selectedStatusIds,
        source: selectedSources,
        tags: selectedTagIds,
        assignee: selectedAssigneeIds,
        ads: selectedAdIds,
        dateRangeType,
        dateRange: selectedDateRange
      })?.filter_value
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsList]);

  if (didInitialCheck && showEmptyFromInitial && !isRestrictionConfigLoading) {
    return (
      <LeadListEmptyView
        organizationId={organizationId}
        onAddManually={() => navigate(LEAD_MANAGEMENT.LEAD_ADD)}
        showMetaOption={isMetaLeadIngestionEnabled}
        onSyncFromMeta={handleSyncFromMeta}
        onCsvUploadSuccess={() => handleCsvUploadSuccess(true)}
        onMetaSetupConfirm={handleMetaSetupConfirm}
        metaProvider={providerData}
        restrictLeadCreationForAgent={isAssigneeSelectionDisabled}
        isMetaSetupModalOpen={isMetaSetupModalOpen}
        onMetaSetupModalClose={() => setIsMetaSetupModalOpen(false)}
      />
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <Head title="Leads" />
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-4">
            <div className="text-2xl font-bold text-content">Leads</div>
          </div>
          {!isAssigneeSelectionDisabled && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <DropDownActionButton
                  label="Add Lead"
                  dropdownOptions={getAddLeadOptionsForOrg(
                    orgFeatureFlags,
                    providerData
                  )}
                  leftIconName="add-icon"
                  onDropdownSelect={(option) => handleAddOption(option.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 rounded-2xl py-6">
        <LeadFilters
          search={search}
          setSearch={setSearch}
          sourceOptions={sourceOptions}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          adOptions={adOptions}
          selectedSources={selectedSources}
          selectedStatusIds={selectedStatusIds}
          selectedTagIds={selectedTagIds}
          selectedAdIds={selectedAdIds}
          setSelectedSources={setSelectedSources}
          setSelectedStatusIds={setSelectedStatusIds}
          setSelectedTagIds={setSelectedTagIds}
          setSelectedAdIds={setSelectedAdIds}
          adsSelectMode={adsSelectMode}
          setAdsSelectMode={setAdsSelectMode}
          excludedAdIds={excludedAdIds}
          setExcludedAdIds={setExcludedAdIds}
          selectedAssigneeIds={selectedAssigneeIds}
          setSelectedAssigneeIds={setSelectedAssigneeIds}
          assigneeOptions={assigneeOptions}
          setDateRange={setDateRange}
          setDateRangeType={setDateRangeType}
          dateRangeType={dateRangeType}
          resultsCount={leadsList?.pagination?.total_rows}
          fetchAds={fetchAds}
          loadMoreAds={loadMoreAds}
          adsHasMore={adsHasMore}
          adsIsLoading={adsIsLoading}
          adsTotalCount={adsTotalCount}
        />
        <LeadTable
          handleMenuIconClick={() => {}}
          leadDetails={leadsList}
          page={page}
          setPage={handlePagination}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onUpdateAssignee={handleUpdateAssignee}
          handleAddTagButtonClick={handleAddTagButtonClick}
          agentsList={agentsList?.sales_agents || []}
          handleDeleteLead={handleDeleteLead}
          isListLoading={isLeadsLoading || isFetchingLeads}
          isAssigneeDisabled={isAssigneeSelectionDisabled}
          hideDeleteAction={isLeadDeletionDisabled}
        />
        {selectedRows.length > 1 && (
          <div className="absolute inset-x-0">
            {/* Bulk bar: any role with leads list access; 2+ rows selected */}
            <MultipleLeadSelectionBottomBar
              selectedCount={selectedRows.length}
              onClearSelection={() => setSelectedRows([])}
              onTagsAction={onTagsAction}
              onAssigneesAction={onAssigneesAction}
              onStatusAction={onStatusAction}
              onLeadsAction={onLeadsAction}
              selectedSection={selectedSection}
              hideAssigneesAction={isAssigneeSelectionDisabled}
            />
          </div>
        )}
      </div>
      {isTagModalOpen && (
        <LeadsAddTagModal
          isOpen={isTagModalOpen}
          onClose={() => {
            setIsTagModalOpen(false);
          }}
          onAddTags={handleAddTags}
          existingTags={getExistingTagsOfSelectedLead()}
          tagsList={tagsList}
          isBulkEdit={selectedRows.length > 1}
          existingSelectedTags={[]}
        />
      )}
      {isCsvUploadModalOpen && (
        <LeadBulkUploadModal
          isOpen={isCsvUploadModalOpen}
          handleCancel={handleCsvModalClose}
          onCreateSuccess={handleCsvUploadSuccess}
          organizationId={organizationId}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          showDeleteModal={isDeleteModalOpen}
          handleCancel={() => setIsDeleteModalOpen(false)}
          handleConfirmDelete={onConfirmDeleteLead}
          title="Delete Lead"
          message={getDeleteModalMessage()}
        />
      )}
      {isMetaSetupModalOpen && (
        <SetupMetaIntegrationModal
          isOpen={isMetaSetupModalOpen}
          onClose={() => setIsMetaSetupModalOpen(false)}
          onConfirm={handleMetaSetupConfirm}
        />
      )}
    </div>
  );
};

export default LeadList;
